/**
 * Writing & Graphing Inequalities generator.
 *
 * Number-line inequalities in both directions, as deterministic multiple
 * choice (per the feasibility report: graph questions are selection, not
 * construction, in V1):
 *
 *   G1: inequality -> graph   show "x > 2", pick the matching number line
 *   G2: graph -> inequality   show a number line, pick the matching inequality
 *   G3: words -> inequality   "at most 5", pick the inequality
 *
 * The correct choice and the distractors are built by varying the boundary and
 * the open/closed + direction independently, which produces the classic
 * confusions (wrong direction, wrong open/closed). Choices carry a PlotModel
 * (G1) or inequality LaTeX (G2/G3); grading is exact choice matching.
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import type { Op } from "../engine/inequality";
import { rayNumberLine, windowFor } from "../graph/number-line";
import { opLatex } from "../engine/inequality";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Choice,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "writing-graphing-inequalities" as const;
const VERSION = "1.0.0";
const VAR = "x";
const OPS: readonly Op[] = ["<", "<=", ">", ">="];

type Family = "G1" | "G2" | "G3";

interface Cfg {
  readonly families: readonly Family[];
  readonly boundMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["G1", "G2"], boundMax: 6 },
  standard: { families: ["G1", "G2", "G3"], boundMax: 9 },
  hard: { families: ["G2", "G3"], boundMax: 12 },
  challenge: { families: ["G2", "G3"], boundMax: 12 },
};

const WORDS: Record<Op, string> = {
  "<": "less than",
  "<=": "at most",
  ">": "greater than",
  ">=": "at least",
};

function ineqLatex(op: Op, value: Rational): string {
  return `${VAR} ${opLatex(op)} ${value.toLatex()}`;
}

/** Distinct (op,value) distractors sharing the boundary but varying op, plus a shifted boundary. */
function distractorOps(correct: Op): Op[] {
  // Common confusions: wrong direction (flip), wrong strictness.
  const flipped: Op = correct === "<" ? ">" : correct === "<=" ? ">=" : correct === ">" ? "<" : "<=";
  const strictnessSwap: Op = correct === "<" ? "<=" : correct === "<=" ? "<" : correct === ">" ? ">=" : ">";
  return [flipped, strictnessSwap];
}

export class WritingGraphingInequalitiesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    const rng = new Rng(ctx.seed);
    const cfg = DIFF[ctx.difficulty];
    const family = rng.pick(cfg.families);
    const op = rng.pick(OPS);
    const value = rat(rng.intNonZero(-cfg.boundMax, cfg.boundMax));
    const win = windowFor([value]);

    let prompt: Question["prompt"];
    let choices: Choice[];
    let stepLatex: string;

    if (family === "G1") {
      // inequality -> graph: choices are number-line plots.
      prompt = {
        text: `Which number line shows ${ineqLatex(op, value)}?`,
        latex: `\\text{Which number line shows } ${ineqLatex(op, value)}\\text{?}`,
      };
      const correct: Choice = {
        id: "correct",
        graph: rayNumberLine(op, value, win),
      };
      const distractors: Choice[] = distractorOps(op).map((dop, i) => ({
        id: `d${i}`,
        graph: rayNumberLine(dop, value, win),
      }));
      // One more: correct direction/strictness but shifted boundary.
      const shifted = value.equals(rat(0)) ? rat(1) : value.add(rat(op === "<" || op === "<=" ? 1 : -1));
      distractors.push({ id: "d2", graph: rayNumberLine(op, shifted, win) });
      choices = rng.shuffle([correct, ...distractors]);
      stepLatex = ineqLatex(op, value);
    } else {
      // G2/G3 -> inequality: choices are inequality LaTeX. Prompt shows a graph
      // (G2) or a verbal phrase (G3).
      if (family === "G2") {
        prompt = {
          text: "Which inequality does this number line show?",
          latex: "\\text{Which inequality matches the number line?}",
        };
      } else {
        prompt = {
          text: `Which inequality means "${VAR} is ${WORDS[op]} ${value.toString()}"?`,
          latex: `\\text{Which inequality means “}${VAR}\\text{ is ${WORDS[op]} ${value.toString()}”?}`,
        };
      }
      const correct: Choice = { id: "correct", latex: ineqLatex(op, value) };
      const distractors: Choice[] = distractorOps(op).map((dop, i) => ({
        id: `d${i}`,
        latex: ineqLatex(dop, value),
      }));
      const shifted = value.equals(rat(0)) ? rat(1) : value.add(rat(1));
      distractors.push({ id: "d2", latex: ineqLatex(op, shifted) });
      choices = rng.shuffle([correct, ...distractors]);
      stepLatex = ineqLatex(op, value);
    }

    const q: Question = {
      id: `${TOPIC}-${ctx.seed}-${family}`,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family,
      difficulty: ctx.difficulty,
      prompt,
      answerType: "choice",
      answer: { kind: "choice", value: "correct" },
      choices,
      steps: [
        {
          description:
            "Open circle for < or >, closed circle for ≤ or ≥; shade toward the solutions.",
          latex: stepLatex,
        },
      ],
      hints: [
        { level: 1, text: "Decide open (<, >) vs closed (≤, ≥) circle, then which way to shade." },
        { level: 2, text: "Shade toward larger numbers for > / ≥ and toward smaller for < / ≤." },
      ],
      subskills: topic.subskills,
    };
    // G2 shows the number line as the prompt graph.
    if (family === "G2") {
      return { ...q, graph: rayNumberLine(op, value, win) };
    }
    return q;
  }

  signature(q: Question): QuestionSignature {
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure: q.id };
  }
}

export const writingGraphingInequalities = new WritingGraphingInequalitiesGenerator();
